/**
 * ChatView — ported from `ui/paradex/src/components/terminal/ChatView.tsx`.
 *
 * Host couplings have been lifted out behind the package seams:
 *  - agent state/actions  → `../../providers/contexts`
 *  - host identity/config → `useTerminalConfig()` (`TerminalConfig`)
 *  - wire types           → `@paradigm/terminal-core`
 *
 * Optional host features (generative UI, widgets, positions, subkey card,
 * mcp-app) are gated on `config.features` / `config.capabilities`. When a
 * capability is absent the feature renders a clearly-marked placeholder and an
 * `// INJECTION POINT:` comment marks where the host renderer plugs in.
 */
import {
  FocusEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { keyframes } from 'styled-components';

import { THIN_SCROLLBAR } from '../../styles/scrollbar';

import { useTerminalConfig } from '../../context/config-context';
import { useAgentActions, useAgentState } from '../../providers/contexts';

import ModelPicker from './ModelPicker';

import { PairingHelpBanner, TokenMismatchBanner } from './banners';
import { isPairingRequiredError, isTokenMismatchError } from './banners/errors';
import {
  buildExamplePrompts,
  deriveGreetingName,
  extractPositionMarkets,
  formatChatDateLabel,
  greetingForHour,
  isTextEntryTarget,
  managesOwnFocus,
} from './chat-utils';
import { Container } from './chat.styles';
import { SendIcon, SlashIcon, StopIcon } from './icons';
import { MessageBubble, UiRenderer } from './messages/MessageBubble';
import {
  BUILT_IN_SLASH_COMMANDS,
  buildSkillCommandSuggestions,
  buildSlashCommandSuggestions,
  getSlashCommandQuery,
  type SlashCommandSuggestion,
} from './slash-commands';

export default function ChatView() {
  const agentState = useAgentState();
  const agentActions = useAgentActions();
  // Host coupling `useWalletView().paradexAddress` → config `accountId`.
  const { accountId } = useTerminalConfig();

  // The auth gate (see Terminal) only mounts this once paired with an account,
  // so accountId is present; guard defensively for isolated test renders.
  if (accountId == null) return null;

  return (
    <ChatInterface
      state={agentState}
      actions={agentActions}
      accountId={accountId}
    />
  );
}

/** Main chat interface */
function ChatInterface({
  state,
  actions,
  accountId,
}: {
  readonly state: ReturnType<typeof useAgentState>;
  readonly actions: ReturnType<typeof useAgentActions>;
  readonly accountId: string;
}) {
  const config = useTerminalConfig();
  const [input, setInput] = useState('');
  const [isMultilineInput, setIsMultilineInput] = useState(false);
  const [skillCommands, setSkillCommands] = useState<
    readonly SlashCommandSuggestion[]
  >([]);
  const [slashIndex, setSlashIndex] = useState(0);
  const [manualSlashOpen, setManualSlashOpen] = useState(false);
  // How many of the most-recent messages to actually render. The provider
  // holds the full transcript (up to ~1500); we only mount a window of it so
  // switching into a long chat renders O(window) bubbles instead of O(all),
  // and streaming reconciles the same bounded set. "Load earlier" grows this.
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_MESSAGES);
  // Sticky once the user has loaded the whole transcript: keeps every message
  // rendered as new ones stream in, so the window can't slip back below the
  // total and make the "Load earlier" button flicker back into view.
  const [showAllMessages, setShowAllMessages] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const composerResizeObserverRef = useRef<ResizeObserver | null>(null);
  const hasAutoScrolledRef = useRef(false);
  // Suppresses the floating-date pill while the auto-scroll effect is doing
  // programmatic `scrollTop` writes. Without this, every session switch + the
  // mount jump-to-bottom flashes the pill for ~1.2s.
  const suppressDatePillRef = useRef(false);
  // The session id we last force-scrolled to the bottom, and that session's
  // last-seen message count — used to distinguish "switched into a chat"
  // (always jump to bottom) from "new message in the current chat" (only stick
  // when already near the bottom).
  const lastScrolledSessionRef = useRef<string | null>(null);
  const prevMessageCountRef = useRef(0);
  // Set by "Load earlier" to the pre-expansion distance-from-bottom, so the
  // anchoring layout effect can restore the viewport after older messages
  // mount above the fold (otherwise growing the window shoves content down).
  const scrollAnchorRef = useRef<number | null>(null);

  // Host coupling `useOpenPositionsStore` → `capabilities.positionsFeed`.
  // When absent, positions context is omitted entirely (no markets ⇒ generic
  // example prompts). The feed returns `readonly unknown[]`; we defensively
  // pull a `market` string off each entry.
  const positions = config.capabilities?.positionsFeed?.usePositions();
  const examplePrompts = useMemo(() => {
    const markets = extractPositionMarkets(positions);
    return buildExamplePrompts(markets);
  }, [positions]);

  // NOTE: the Paradex host re-focused the composer when its drawer tab
  // switched to 'chat' (via `useTerminalDrawerView`). That drawer-context is
  // host-specific and has no package seam, so the tab-switch refocus effect is
  // dropped. The connect-time + session-switch focus below covers the common
  // cases.
  // TODO(host-injection): add an optional "active surface" signal to the
  // config if hosts need composer refocus on tab activation.

  // Auto-connect on mount if URL is set but not connected.
  // Intentional mount-only effect: adding state/actions to deps would
  // re-trigger the connection on every status change, causing reconnect loops.
  useEffect(() => {
    if (state.agentUrl != null && state.status === 'disconnected') {
      actions.connect();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refocus the composer when the active session changes.
  useEffect(() => {
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [state.activeSessionId]);

  // Auto-scroll to bottom on new messages.
  //
  // First fire (mount, with messages already in context from prior session):
  // hide the list, jump to the bottom, then reveal. Without this the user
  // sees the scroll zip through the entire chat history while the drawer
  // slide-in animation is still playing.
  //
  // Subsequent fires (new incoming messages): smooth-scroll via the end anchor.
  useLayoutEffect(() => {
    const list = messageListRef.current;
    if (list == null) return;
    // The full-terminal overlay manages its own scrolling; don't hijack it.
    // Reset the first-fire flag so we re-arm the initial jump-to-bottom when
    // the overlay closes and the message list comes back.
    if (state.fullTerminalUi != null) {
      hasAutoScrolledRef.current = false;
      return;
    }

    // Mute the date-pill scroll listener for the duration of any programmatic
    // scroll this effect does. 300ms covers the rAF chain in the first-fire
    // branch; refs reset on the next effect run, so a leaked timeout is
    // harmless.
    suppressDatePillRef.current = true;
    window.setTimeout(() => {
      suppressDatePillRef.current = false;
    }, 300);

    const switchedSession =
      lastScrolledSessionRef.current !== state.activeSessionId;
    const wasEmpty = prevMessageCountRef.current === 0;
    prevMessageCountRef.current = state.messages.length;

    if (hasAutoScrolledRef.current) {
      // Switched into a different chat — or its transcript just arrived after
      // an empty switch (lazy remote load) — always jump to the bottom so the
      // latest messages are visible, regardless of the prior scroll position.
      if (switchedSession || (wasEmpty && state.messages.length > 0)) {
        lastScrolledSessionRef.current = state.activeSessionId;
        list.scrollTop = list.scrollHeight;
        return;
      }

      // Same chat, new/edited messages: stick to the bottom ONLY when the user
      // is already near it. Otherwise a non-append `messages` change (e.g.
      // approve/deny replacing the array, or a streaming edit) must not yank a
      // scrolled-up reader down. Scroll the inner list — never the page.
      const distanceFromBottom =
        list.scrollHeight - list.scrollTop - list.clientHeight;
      if (distanceFromBottom <= AUTO_SCROLL_NEAR_BOTTOM_PX) {
        list.scrollTop = list.scrollHeight;
      }
      return;
    }

    // First fire: hide → position → reveal after two frames (enough for the
    // drawer to report a stable layout height).
    lastScrolledSessionRef.current = state.activeSessionId;
    list.style.visibility = 'hidden';
    list.scrollTop = list.scrollHeight;

    let frame1 = requestAnimationFrame(() => {
      list.scrollTop = list.scrollHeight;
      frame1 = requestAnimationFrame(() => {
        list.scrollTop = list.scrollHeight;
        list.style.visibility = '';
        hasAutoScrolledRef.current = true;
      });
    });

    return () => cancelAnimationFrame(frame1);
  }, [state.messages, state.fullTerminalUi, state.activeSessionId]);

  // Floating date pill: shows the date of the topmost visible message while
  // the user scrolls, then fades after a short idle. The auto-scroll effect
  // sets `suppressDatePillRef` to filter out its own programmatic scrolls so
  // session switches don't flash the pill.
  const [floatingDateTs, setFloatingDateTs] = useState<number | null>(null);
  const [datePillVisible, setDatePillVisible] = useState(false);
  // Drop any stale date as soon as the session changes — otherwise the prior
  // chat's date would briefly show if its transcript hasn't loaded yet.
  useEffect(() => {
    setFloatingDateTs(null);
    setDatePillVisible(false);
  }, [state.activeSessionId]);
  useEffect(() => {
    const el = messageListRef.current;
    if (el == null) return undefined;
    let hideTimer: number | null = null;
    const updateTopDate = () => {
      const rows = el.querySelectorAll<HTMLElement>('[data-msg-ts]');
      if (rows.length === 0) {
        setFloatingDateTs(null);
        return;
      }
      const top = el.getBoundingClientRect().top;
      for (const row of rows) {
        if (row.getBoundingClientRect().bottom > top + 1) {
          const ts = Number(row.dataset.msgTs);
          if (Number.isFinite(ts)) setFloatingDateTs(ts);
          break;
        }
      }
    };
    const onScroll = () => {
      if (suppressDatePillRef.current) return;
      updateTopDate();
      setDatePillVisible(true);
      if (hideTimer != null) window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => setDatePillVisible(false), 1200);
    };
    updateTopDate();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (hideTimer != null) window.clearTimeout(hideTimer);
    };
  }, [state.messages.length, state.activeSessionId]);

  useEffect(() => {
    if (state.status !== 'authenticated') {
      setSkillCommands([]);
      return;
    }

    let cancelled = false;

    actions
      .request<unknown>('skills.status')
      .then((payload) => {
        if (cancelled) return;
        setSkillCommands(buildSkillCommandSuggestions(payload));
      })
      .catch(() => {
        if (cancelled) return;
        setSkillCommands([]);
      });

    return () => {
      cancelled = true;
    };
  }, [actions, state.status, state.agentUrl]);

  // Only accept input once the gateway has authenticated us. During
  // 'connected'/'authenticating' the handshake is still in flight and any
  // chat.send would be dropped server-side.
  const isConnected = state.status === 'authenticated';
  // Only the explicit "Thinking" indicator counts as in-flight from the
  // user's POV. `state.processing` is set optimistically in
  // `sendMessage` and cleared on first delta / done / error — i.e. it's
  // alive only between "user pressed Send" and "first token of the
  // response". Background frames (heartbeat echoes, unprompted system
  // messages) never set processing, so they no longer flip the button
  // to Stop.
  const isProcessing = state.processing != null;

  // Focus the chat input as soon as it becomes enabled, so opening the
  // terminal (e.g. via `~`) lands the cursor in the typing box without an
  // extra click. The textarea has `disabled={!isConnected}`, so a focus
  // call before authentication is a no-op — we wait for isConnected.
  useEffect(() => {
    if (isConnected) inputRef.current?.focus();
  }, [isConnected]);

  // Sticky composer focus: the chat input is the terminal's default typing
  // target, so when a click moves focus to another *control* (a button, a
  // chip) pull focus straight back. Crucially, we do NOT reclaim focus when
  // the user is selecting text in the transcript — that blur has no
  // `relatedTarget` (focus goes to the document body), and re-focusing the
  // textarea would collapse the selection. Selecting text stays normal; the
  // next keystroke still lands in the composer via the type-ahead listener.
  const handleComposerBlur = useCallback(
    (e: FocusEvent<HTMLTextAreaElement>) => {
      if (!isConnected) return;
      const next = e.relatedTarget;
      if (next == null) return;
      if (isTextEntryTarget(next)) return;
      if (managesOwnFocus(next)) return;
      const root =
        containerRef.current?.closest('[data-terminal-root]') ?? null;
      if (root != null && !root.contains(next)) return;
      requestAnimationFrame(() => {
        if (document.activeElement !== inputRef.current) {
          inputRef.current?.focus();
        }
      });
    },
    [isConnected],
  );

  // Type-ahead: when the user starts typing while focus is somewhere
  // non-editable (e.g. after selecting transcript text, or clicking empty
  // space), route the keystroke into the composer instead of dropping it.
  useEffect(() => {
    if (!isConnected) return undefined;
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      const composer = inputRef.current;
      if (composer == null) return;
      const active = document.activeElement;
      if (active === composer) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return;
      if (e.key.length !== 1) return;
      if (isTextEntryTarget(active)) return;
      if (managesOwnFocus(active)) return;
      const root =
        containerRef.current?.closest('[data-terminal-root]') ?? null;
      if (
        root != null &&
        active != null &&
        active !== document.body &&
        !root.contains(active)
      ) {
        return;
      }
      e.preventDefault();
      setInput((prev) => prev + e.key);
      composer.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isConnected]);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (trimmed.length === 0) return;
    actions.sendMessage(trimmed);
    setInput('');
    inputRef.current?.focus();
  }, [input, actions]);

  const handleExampleClick = useCallback(
    (prompt: string) => {
      if (state.status !== 'authenticated') return;
      actions.sendMessage(prompt);
      setInput('');
      inputRef.current?.focus();
    },
    [actions, state.status],
  );

  const slashCommandQuery =
    getSlashCommandQuery(input) ?? (manualSlashOpen ? '' : null);
  const slashSuggestions = useMemo(
    () =>
      buildSlashCommandSuggestions(
        [...BUILT_IN_SLASH_COMMANDS, ...skillCommands],
        slashCommandQuery,
      ),
    [skillCommands, slashCommandQuery],
  );
  const showSlashCommands =
    isConnected && slashCommandQuery != null && slashSuggestions.length > 0;

  useEffect(() => {
    setSlashIndex(0);
  }, [slashCommandQuery]);

  useEffect(() => {
    if (slashIndex >= slashSuggestions.length) {
      setSlashIndex(Math.max(0, slashSuggestions.length - 1));
    }
  }, [slashIndex, slashSuggestions.length]);

  const selectSlashCommand = useCallback(
    (suggestion: SlashCommandSuggestion) => {
      setInput(suggestion.insertText);
      setManualSlashOpen(false);
      inputRef.current?.focus();
    },
    [],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (showSlashCommands) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashIndex((index) => (index + 1) % slashSuggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashIndex(
          (index) =>
            (index - 1 + slashSuggestions.length) % slashSuggestions.length,
        );
        return;
      }
      if (e.key === 'Tab') {
        // Tab still completes-to-input so the user can type arguments
        // after (e.g. `/model <id>`) before sending.
        e.preventDefault();
        const suggestion = slashSuggestions[slashIndex];
        if (suggestion != null) {
          selectSlashCommand(suggestion);
        }
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        // Enter sends the highlighted suggestion directly — one
        // keystroke from open-menu to executed command for arg-less
        // commands like `/help` or `/model`. We're inside
        // `showSlashCommands`, which requires `isConnected`, so no
        // separate auth gate needed here.
        e.preventDefault();
        const suggestion = slashSuggestions[slashIndex];
        if (suggestion != null) {
          actions.sendMessage(suggestion.insertText);
          setInput('');
          setManualSlashOpen(false);
          inputRef.current?.focus();
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setInput('');
        setManualSlashOpen(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // The textarea auto-grows via CSS `field-sizing: content` (see ChatInput) —
  // there is no per-keystroke JS height measurement. We still need to know when
  // it has grown past one line so the send/slash buttons pin to the bottom
  // instead of vertically centering; a ResizeObserver reports that in its own
  // batched phase, off the keystroke critical path, and only when the size
  // actually changes. (The previous per-keystroke layout effect read
  // `scrollHeight` on every character, forcing a full-document reflow — that's
  // what caused the input lag this PR fixes.)
  //
  // Attach via a ref callback rather than a mount-only effect: the composer's
  // <textarea> unmounts and remounts when the empty-state layout flips
  // (`CenteredEmpty` <-> `InputArea` are structurally different subtrees), so a
  // one-shot observer would stay bound to the detached node and leave
  // `isMultilineInput` stale after the first send. The callback re-runs on
  // every node-identity change, keeps `inputRef.current` in sync, and survives
  // the reverse flip. The `useCallback([])` identity keeps React from
  // re-invoking it on ordinary re-renders.
  const attachComposer = useCallback((ta: HTMLTextAreaElement | null) => {
    inputRef.current = ta;
    composerResizeObserverRef.current?.disconnect();
    composerResizeObserverRef.current = null;
    if (ta == null || typeof ResizeObserver === 'undefined') return;
    // Pin the initial value so a remount onto pre-filled text (e.g. a restored
    // draft) aligns correctly without waiting for the first resize event.
    setIsMultilineInput(ta.scrollHeight > SINGLE_LINE_TEXTAREA_HEIGHT);
    const ro = new ResizeObserver(() => {
      // scrollHeight is safe to read here: the observer fires after layout is
      // computed, so this returns the cached value without forcing a reflow,
      // and it runs off the typing path (only on an actual size change).
      setIsMultilineInput(ta.scrollHeight > SINGLE_LINE_TEXTAREA_HEIGHT);
    });
    ro.observe(ta);
    composerResizeObserverRef.current = ro;
  }, []);

  // Claude-style centered empty state. When the session has no messages
  // AND no banners to surface, drop the message-list region and float
  // the composer (+ examples + model picker) vertically centered, with a
  // time-of-day greeting above it. The first send flips this off and the
  // composer snaps to its standard bottom position.
  const isEmpty =
    state.messages.length === 0 &&
    state.fullTerminalUi == null &&
    !isTokenMismatchError(state.errorMessage) &&
    !isPairingRequiredError(state.errorMessage);
  // Host profile first (cloud flow: Clerk name → email local part); the
  // gateway-URL derivation only helps on per-user gateway hosts — behind the
  // shared gateway-proxy it would greet everyone as "Gateway".
  const profileName =
    config.user?.displayName?.trim() ||
    config.user?.email?.split('@')[0]?.trim() ||
    '';
  const greetingName =
    profileName.length > 0 ? profileName : deriveGreetingName(state.agentUrl);
  // Snapshot the time-of-day greeting once per session so it can't flip
  // mid-conversation if the user crosses noon / 6pm while the page is open.
  const greetingTime = useMemo(
    () => greetingForHour(new Date().getHours()),
    [],
  );
  const greeting =
    greetingName.length > 0 ? `${greetingTime}, ${greetingName}` : greetingTime;

  // Composer subtree extracted so we can mount it in both layouts without
  // duplicating ~70 lines of JSX. State (input, slash menu, refs) is closed
  // over from the surrounding component scope.
  const composerNode = (
    <Composer>
      {showSlashCommands && (
        <SlashCommandMenu role="listbox" aria-label="Slash commands">
          {slashSuggestions.map((suggestion, index) => (
            <SlashCommandOption
              key={suggestion.id}
              type="button"
              role="option"
              aria-selected={index === slashIndex}
              $active={index === slashIndex}
              onMouseDown={(event) => {
                event.preventDefault();
                selectSlashCommand(suggestion);
              }}
              onMouseEnter={() => setSlashIndex(index)}
            >
              <SlashCommandName>{suggestion.label}</SlashCommandName>
              <SlashCommandDescription>
                {suggestion.description}
              </SlashCommandDescription>
              {suggestion.kind === 'skill' && (
                <SlashCommandBadge>skill</SlashCommandBadge>
              )}
            </SlashCommandOption>
          ))}
        </SlashCommandMenu>
      )}
      <SlashTriggerButton
        type="button"
        onClick={() => {
          setManualSlashOpen((prev) => !prev);
          inputRef.current?.focus();
        }}
        disabled={!isConnected}
        aria-label="Show slash commands"
        title="Show slash commands"
        aria-pressed={manualSlashOpen}
        $alignBottom={isMultilineInput}
      >
        <SlashIcon />
      </SlashTriggerButton>
      {/* Fake caret that always blinks, independent of real DOM focus — the
          composer regains keystrokes via the type-ahead listener regardless
          of where focus visually is, so this reflects real behavior rather
          than lying about it. Gated on `isConnected`: while disconnected the
          textarea is `disabled` (can't take real focus, and the type-ahead
          listener bails too), so showing this would advertise a typing
          target that drops every keystroke. Hidden via `Composer:focus-within`
          (CSS only, no JS focus tracking) whenever the real textarea has
          focus, so the two carets never show at once. Absolutely positioned
          inside ChatInputWrap (not a flex sibling) so it never perturbs the
          textarea's own layout/padding. */}
      <ChatInputWrap>
        {isConnected && input.length === 0 && <FakeCaret aria-hidden="true" />}
        <ChatInput
          ref={attachComposer}
          data-chat-input
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            if (manualSlashOpen && !e.target.value.startsWith('/')) {
              setManualSlashOpen(false);
            }
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleComposerBlur}
          placeholder={isConnected ? 'Message your agent...' : 'Connecting...'}
          disabled={!isConnected}
          rows={1}
          aria-autocomplete="list"
          aria-expanded={showSlashCommands}
        />
      </ChatInputWrap>
      <SendButton
        type="button"
        onClick={isProcessing ? actions.stopProcessing : handleSend}
        disabled={
          isProcessing ? false : !isConnected || input.trim().length === 0
        }
        aria-label={isProcessing ? 'Stop' : 'Send message'}
        $alignBottom={isMultilineInput}
      >
        {isProcessing ? <StopIcon /> : <SendIcon />}
      </SendButton>
    </Composer>
  );

  const examplesNode =
    state.messages.length === 0 &&
    state.fullTerminalUi == null &&
    isConnected ? (
      <ExamplesRow>
        {examplePrompts.map((ex) => (
          <ExampleChip
            key={ex.prompt}
            type="button"
            onClick={() => handleExampleClick(ex.prompt)}
            title={ex.prompt}
          >
            <ExampleLabel>{ex.label}</ExampleLabel>
            {ex.subtitle.length > 0 && (
              <ExampleSubtitle>{ex.subtitle}</ExampleSubtitle>
            )}
          </ExampleChip>
        ))}
      </ExamplesRow>
    ) : null;

  const modelPickerNode =
    state.models.length > 0 ? (
      <ComposerFooter>
        <ModelPicker
          models={state.models}
          selectedId={state.selectedModelId}
          onSelect={actions.setSelectedModel}
        />
      </ComposerFooter>
    ) : null;

  // Always render the private-chat notice row at its full height so the
  // centered empty-state composer doesn't jump vertically when switching
  // between persistent and temporary chats. The badge inside flips between
  // visible (temporary) and hidden (otherwise) — the row keeps its slot.
  const isTemporarySession =
    state.sessions.find((s) => s.sessionId === state.activeSessionId)
      ?.isTemporary === true;

  // Reset the render window whenever we switch chats so a freshly-opened
  // session starts by mounting only its most-recent slice.
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_MESSAGES);
    setShowAllMessages(false);
  }, [state.activeSessionId]);

  // Grow the window toward the top. Capture the current distance-from-bottom
  // first; the anchoring layout effect restores it after the older messages
  // render, keeping the user's scroll position stable. Once an expansion would
  // reach the top of the transcript, flip to the sticky "show all" mode so the
  // button doesn't reappear when later messages stream in.
  const loadEarlierMessages = useCallback(() => {
    const list = messageListRef.current;
    scrollAnchorRef.current =
      list != null ? list.scrollHeight - list.scrollTop : null;
    if (visibleCount + MESSAGE_PAGE_SIZE >= state.messages.length) {
      setShowAllMessages(true);
    } else {
      setVisibleCount((c) => c + MESSAGE_PAGE_SIZE);
    }
  }, [visibleCount, state.messages.length]);

  // After a "Load earlier" expansion, pin the viewport to where it was. Only
  // fires when the anchor was set by loadEarlierMessages — the reset-on-switch
  // and initial mount leave it null, so this no-ops there and the standard
  // jump-to-bottom auto-scroll effect owns those cases.
  useLayoutEffect(() => {
    const list = messageListRef.current;
    const anchor = scrollAnchorRef.current;
    if (list == null || anchor == null) return;
    list.scrollTop = list.scrollHeight - anchor;
    scrollAnchorRef.current = null;
  }, [visibleCount, showAllMessages]);

  // The rendered window: the last `visibleCount` messages (or all, once the
  // user has loaded the full transcript). The full transcript stays in the
  // provider; slicing is a cheap in-memory op, not a fetch.
  const visibleMessages = useMemo(
    () =>
      showAllMessages || visibleCount >= state.messages.length
        ? state.messages
        : state.messages.slice(-visibleCount),
    [state.messages, visibleCount, showAllMessages],
  );
  const hasEarlierMessages = state.messages.length > visibleMessages.length;

  // The message region depends only on server-driven state, never on the
  // composer's `input`. Memoizing it means a keystroke (which re-renders
  // ChatInterface via setInput) reuses the exact same element reference, so
  // React bails out of reconciling the entire message list instead of
  // re-running the message `.map` and diffing every bubble on every
  // character. It still recomputes when messages/processing/etc. actually
  // change (e.g. streaming), which is correct.
  const messageRegion = useMemo(
    () =>
      state.fullTerminalUi != null ? (
        <FullTerminalOverlay>
          <FullTerminalBar>
            <FullTerminalTitle>
              {state.fullTerminalUi.ui.mode === 'mcp-app'
                ? (state.fullTerminalUi.ui.title ?? 'Agent view')
                : 'Agent layout'}
            </FullTerminalTitle>
            <SmallButton onClick={actions.dismissFullTerminalUi}>
              Return to chat
            </SmallButton>
          </FullTerminalBar>
          <FullTerminalBody>
            <UiRenderer
              ui={state.fullTerminalUi.ui}
              surfaceId={state.fullTerminalUi.messageId}
              sendUiEvent={actions.sendUiEvent}
            />
          </FullTerminalBody>
        </FullTerminalOverlay>
      ) : (
        <MessageList>
          {isTokenMismatchError(state.errorMessage) &&
            state.agentUrl != null && (
              <TokenMismatchBanner
                agentUrl={state.agentUrl}
                accountId={accountId}
                onReconnect={() => {
                  // The internal agent-url store (built from config) owns
                  // token persistence + session clearing; reconnect routes
                  // through it. See TODO(host-injection) on the setup form
                  // about exposing an explicit token-setter seam.
                  actions.connect();
                }}
              />
            )}
          {isPairingRequiredError(state.errorMessage) && (
            <PairingHelpBanner
              accountId={accountId}
              clientIdPrefix={config.clientIdPrefix}
              onRetry={() => {
                actions.connect();
              }}
            />
          )}
          {hasEarlierMessages && (
            <LoadEarlierRow>
              <LoadEarlierButton
                type="button"
                onClick={loadEarlierMessages}
                aria-label="Load earlier messages"
              >
                Load earlier messages
              </LoadEarlierButton>
            </LoadEarlierRow>
          )}
          {visibleMessages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              onApprovalRespond={actions.respondToApproval}
              onUiEvent={actions.sendUiEvent}
            />
          ))}
          {state.processing != null && (
            <ProcessingRow role="status" aria-live="polite">
              <CyclingThinkingLabel />
              {state.processing.detail != null &&
                state.processing.detail.length > 0 && (
                  <ProcessingDetail>
                    {' — '}
                    {state.processing.detail}
                  </ProcessingDetail>
                )}
            </ProcessingRow>
          )}
          <div ref={messagesEndRef} />
        </MessageList>
      ),
    [
      state.fullTerminalUi,
      state.errorMessage,
      state.agentUrl,
      visibleMessages,
      hasEarlierMessages,
      loadEarlierMessages,
      state.processing,
      accountId,
      config.clientIdPrefix,
      actions,
    ],
  );

  return (
    <Container ref={containerRef}>
      <PrivateChatNoticeRow>
        <TemporaryBadge
          $hidden={!isTemporarySession}
          aria-hidden={!isTemporarySession}
        >
          Private Chat - Not Saved
        </TemporaryBadge>
      </PrivateChatNoticeRow>

      {isEmpty ? (
        <CenteredEmpty>
          <Greeting>{greeting}</Greeting>
          <InputAreaInner>
            {composerNode}
            {modelPickerNode}
            {examplesNode}
          </InputAreaInner>
        </CenteredEmpty>
      ) : (
        <>
          <ChatBody ref={messageListRef}>
            <FloatingDateAnchor>
              <FloatingDatePill
                $visible={datePillVisible && floatingDateTs != null}
              >
                {floatingDateTs != null
                  ? formatChatDateLabel(new Date(floatingDateTs))
                  : ''}
              </FloatingDatePill>
            </FloatingDateAnchor>
            {messageRegion}
          </ChatBody>
          {state.transientError != null && (
            <TransientErrorToast role="alert">
              {state.transientError}
            </TransientErrorToast>
          )}
          <InputArea>
            <InputAreaInner>
              {examplesNode}
              {composerNode}
              {modelPickerNode}
            </InputAreaInner>
          </InputArea>
        </>
      )}
    </Container>
  );
}

// --- Styled Components ---

const TransientErrorToast = styled.div`
  margin: 0 16px 8px;
  padding: 8px 12px;
  border-radius: 6px;
  background: var(--bg-error, rgba(220, 38, 38, 0.15));
  color: var(--text-error, #ef4444);
  font-size: 0.8rem;
  line-height: 1.4;
  animation: fadeIn 150ms ease-out;
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// New-chat empty state: a column that takes the remaining vertical space
// below the PrivateChatNoticeRow and centers its children (greeting +
// composer + examples + model picker). Width-capped to keep the composer
// the same width as in the standard bottom-anchored layout so the
// snap-to-bottom on first send isn't a horizontal jump too.
const CenteredEmpty = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  width: 100%;
  max-width: var(--chat-input-max-width, var(--chat-body-max-width, none));
  margin: 0 auto;
  padding: 24px 0;
  gap: 12px;
`;

const Greeting = styled.h2`
  margin: 0;
  padding: 0 16px;
  font-size: 24px;
  font-weight: 400;
  text-align: center;
  color: var(--text-primary);
  letter-spacing: -0.01em;
`;

// Centered "Today" / "Jun 30" pill that floats over the chat scroll area
// while the user is scrolling, then fades after ~1.2s idle. Height is 0 so it
// reserves no layout space; the pill itself overflows downward visually.
const FloatingDateAnchor = styled.div`
  position: sticky;
  top: 8px;
  z-index: 2;
  align-self: center;
  height: 0;
  pointer-events: none;
`;

const FloatingDatePill = styled.div<{ $visible: boolean }>`
  background: var(--bg-color-container);
  border: 1px solid var(--border-color);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 500;
  color: var(--text-secondary);
  line-height: 1;
  white-space: nowrap;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 0.18s ease;
`;

const ChatBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  /* Scroll the full-width body so the scrollbar sits at the panel's right
     edge; message content stays centered via MessageList's max-width. */
  overflow-y: auto;
  ${THIN_SCROLLBAR}
`;

const TemporaryBadge = styled.span<{ $hidden?: boolean }>`
  padding: 3px 10px;
  border-radius: 999px;
  border: none;
  background: var(--hover-bg);
  font-size: 10px;
  font-weight: 500;
  color: var(--text-primary);
  line-height: 1;
  white-space: nowrap;
  /* When hidden, keep the badge's exact box so the row's vertical slot is
     reserved — prevents the centered empty-state composer from jumping
     between persistent and temporary chats. */
  visibility: ${(p) => (p.$hidden === true ? 'hidden' : 'visible')};
`;

// Centers the "Private Chat - Not Saved" pill in its own row at the top of
// the chat pane.
const PrivateChatNoticeRow = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  padding: 8px 12px 0;
`;

const SmallButton = styled.button`
  padding: 2px 8px;
  border: 1px solid var(--button-color-dark);
  border-radius: 6px;
  background: none;
  color: var(--text-label);
  font-size: 11px;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }
`;

const MessageList = styled.div`
  /* Content column stays centered; the scroll lives on ChatBody so the
     scrollbar hugs the panel's right edge rather than this column. */
  width: 100%;
  max-width: var(--chat-body-max-width, none);
  margin: 0 auto;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProcessingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 0.75rem;
  line-height: 1.4;
  max-width: 85%;
`;

const LoadEarlierRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 4px 0 12px;
`;

const LoadEarlierButton = styled.button`
  padding: 4px 12px;
  border: 1px solid var(--border-color);
  border-radius: 999px;
  background: transparent;
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-family: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--accent-color);
    outline-offset: 2px;
    color: var(--text-primary);
  }

  &:hover {
    color: var(--text-primary);
    border-color: var(--text-secondary);
  }
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const ProcessingLabel = styled.span`
  background: linear-gradient(
    90deg,
    var(--text-secondary) 0%,
    var(--text-secondary) 35%,
    var(--text-primary) 50%,
    var(--text-secondary) 65%,
    var(--text-secondary) 100%
  );
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  animation: ${shimmer} 2.5s linear infinite;
`;

const THINKING_WORDS = [
  'Pondering',
  'Musing',
  'Reflecting',
  'Reasoning',
  'Contemplating',
  'Cogitating',
  'Deliberating',
  'Considering',
  'Ruminating',
  'Brainstorming',
  'Processing',
  'Analyzing',
  'Untangling',
  'Computing',
  'Crunching',
  'Parsing',
  'Synthesizing',
  'Evaluating',
  'Investigating',
  'Decoding',
] as const;

function CyclingThinkingLabel() {
  const [index, setIndex] = useState(() =>
    Math.floor(Math.random() * THINKING_WORDS.length),
  );
  useEffect(() => {
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % THINKING_WORDS.length);
    }, 2500);
    return () => window.clearInterval(id);
  }, []);
  return <ProcessingLabel>{`${THINKING_WORDS[index]}...`}</ProcessingLabel>;
}

const ProcessingDetail = styled.span`
  color: var(--text-secondary);
  opacity: 0.75;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 40ch;
`;

const InputArea = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
`;

const ExamplesRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  margin-top: 24px;
  margin-bottom: 12px;
  overflow-x: auto;
  ${THIN_SCROLLBAR}

  /* Drop the suggestions when the viewport is too short — keeps the
     composer pinned above the footer instead of getting clipped. */
  @media (max-height: 600px) {
    display: none;
  }
`;

const ExampleChip = styled.button`
  flex-shrink: 0;
  width: 180px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 10px 14px;
  border: 1px solid var(--button-color-dark);
  border-radius: 8px;
  background: none;
  font-family: inherit;
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: var(--text-secondary);
  }
`;

const ExampleLabel = styled.span`
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
`;

const ExampleSubtitle = styled.span`
  color: var(--text-label);
  font-size: 12px;
  line-height: 1.3;
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// Fullscreen sets --chat-input-max-width so the composer matches the
// 4-card row width. Drawer doesn't set either var, so this falls back
// to --chat-body-max-width (also unset there), i.e. the drawer input
// fills the full pane width as before.
const InputAreaInner = styled.div`
  width: 100%;
  max-width: var(--chat-input-max-width, var(--chat-body-max-width, none));
  margin: 0 auto;
  padding: 4px;
`;

const Composer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
  background: var(--input-bg-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;

  &:focus-within {
    border-color: var(--text-secondary);
  }
`;

const ComposerFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 6px;
`;

const SlashCommandMenu = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% + 8px);
  z-index: 2;
  max-height: 260px;
  overflow-y: auto;
  padding: 4px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-color-page);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.32);
  ${THIN_SCROLLBAR}
`;

const SlashCommandOption = styled.button<{ $active: boolean }>`
  display: grid;
  grid-template-columns: minmax(8ch, 18ch) minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 36px;
  padding: 7px 8px;
  border: 0;
  border-radius: 6px;
  background: ${({ $active }) =>
    $active ? 'rgba(var(--color-lightest), 0.1)' : 'transparent'};
  color: var(--text-primary);
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: rgba(var(--color-lightest), 0.1);
  }
`;

const SlashCommandName = styled.span`
  font-family: var(--font-mono, monospace);
  font-size: 0.75rem;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SlashCommandDescription = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-secondary);
  font-size: 0.6875rem;
`;

const SlashCommandBadge = styled.span`
  padding: 1px 5px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  color: var(--text-secondary);
  font-size: 0.5625rem;
  line-height: 1.4;
`;

// Wraps ChatInput so FakeCaret can overlay it absolutely instead of joining
// the Composer flex row — an extra flex sibling would shift ChatInput's own
// padding/text-start when the caret mounts/unmounts.
const ChatInputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const ChatInput = styled.textarea`
  width: 100%;
  min-height: 24px;
  max-height: 200px;
  /* Auto-grow to fit content between min/max-height with no JS measurement.
     Supported in Chromium 123+ / Safari 17.4+ (our PWA + embed targets). On
     older engines it's ignored: the textarea stays at the one-line min-height
     and scrolls internally — degraded but functional, and never laggy. */
  field-sizing: content;
  padding: 6px 0;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 0.8125rem;
  font-family: inherit;
  line-height: 1.4;
  resize: none;
  overflow-y: auto;

  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
  }
`;

const caretBlink = keyframes`
  0%, 50% { opacity: 1; }
  50.01%, 100% { opacity: 0; }
`;

// Blinks regardless of real DOM focus; hidden via the parent Composer's
// `:focus-within` once the real textarea is actually focused, so the two
// carets never show at once. See usage site for why this exists.
const FakeCaret = styled.span`
  position: absolute;
  /* 1px clear of the placeholder's leading glyph (ChatInput has no left
     padding, so left: 0 would sit the caret directly on top of it). */
  left: 1px;
  top: 50%;
  width: 1px;
  height: 16px;
  transform: translateY(-50%);
  background: var(--text-primary);
  animation: ${caretBlink} 1s step-end infinite;
  pointer-events: none;

  ${Composer}:focus-within & {
    display: none;
  }
`;

const SINGLE_LINE_TEXTAREA_HEIGHT = 32;

// Render-window sizing. The provider keeps the whole transcript; we only mount
// the most-recent `INITIAL_VISIBLE_MESSAGES` (comfortably more than a
// screenful of scrollback) and reveal older ones a page at a time. Keeps chat
// switches and streaming O(window) rather than O(full history).
const INITIAL_VISIBLE_MESSAGES = 50;
const MESSAGE_PAGE_SIZE = 50;

// Distance (px) from the bottom within which the message list is considered
// "pinned" — only then do non-append updates keep auto-scrolling to the end.
const AUTO_SCROLL_NEAR_BOTTOM_PX = 120;

const SendButton = styled.button<{ $alignBottom?: boolean }>`
  flex-shrink: 0;
  align-self: ${({ $alignBottom }) =>
    $alignBottom === true ? 'flex-end' : 'center'};
  width: 28px;
  height: 28px;
  padding: 0;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 6px;
  background: var(--accent-color);
  color: var(--bg-color-page);
  cursor: pointer;

  &:disabled {
    background: var(--button-color);
    color: var(--text-primary);
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.85;
  }
`;

const SlashTriggerButton = styled.button<{ $alignBottom?: boolean }>`
  flex-shrink: 0;
  align-self: ${({ $alignBottom }) =>
    $alignBottom === true ? 'flex-end' : 'center'};
  width: 22px;
  height: 22px;
  padding: 0;
  display: grid;
  place-items: center;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  opacity: 0.7;

  &:hover:not(:disabled) {
    opacity: 1;
    color: var(--text-primary);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
`;

const FullTerminalOverlay = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  background: var(--bg-color-container);
`;

const FullTerminalBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid var(--divider-color);
`;

const FullTerminalTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const FullTerminalBody = styled.div`
  flex: 1;
  overflow: auto;
  padding: 12px;
  ${THIN_SCROLLBAR}
`;
